<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-lg tw-my-5 tw-rounded-lg">
            <!-- Header -->
            <div class="tw-p-6 tw-border-b tw-border-solid tw-border-gray-200 tw-flex tw-justify-between tw-items-center">
                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-800">{{ $t('forms.category.create-title') }}</h3>
                <button @click="onCancel" class="tw-text-gray-400 hover:tw-text-gray-600 tw-duration-200">
                    <icon icon="solar:close-line-linear" class="tw-text-xl" />
                </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="onSubmit" class="tw-p-6">
                <div class="tw-space-y-6">
                    <!-- Name Field -->
                    <FormField
                        v-model="form.name"
                        type="text"
                        :label="$t('forms.category.name')"
                        :placeholder="$t('forms.category.name-placeholder')"
                        left-icon="solar:widget-bold-duotone"
                        :error="errors.name"
                        required
                    />

                    <!-- Description Field -->
                    <FormField
                        v-model="form.description"
                        type="textarea"
                        :label="$t('forms.category.description')"
                        :placeholder="$t('forms.category.description-placeholder')"
                        :error="errors.description"
                        rows="3"
                    />

                    <!-- Type Field -->
                    <FormSelect
                        v-model="form.type"
                        :options="typeOptions"
                        :label="$t('forms.category.type')"
                        :placeholder="$t('forms.category.type-placeholder')"
                        left-icon="solar:tag-bold-duotone"
                        :error="errors.type"
                    />

                    <!-- Status Field -->
                    <FormSelect
                        v-model="form.status"
                        :options="statusOptions"
                        :label="$t('forms.category.status')"
                        :placeholder="$t('forms.category.status-placeholder')"
                        left-icon="solar:check-circle-bold-duotone"
                        :error="errors.status"
                        required
                    />
                </div>

                <!-- Form Actions -->
                <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-200">
                    <a-button 
                        type="button" 
                        intent="simple" 
                        @click="onCancel"
                        :disabled="isSubmitting"
                    >
                        {{ $t('buttons.cancel') }}
                    </a-button>
                    <a-button 
                        type="submit" 
                        intent="primary" 
                        class="tw-min-w-[160px]"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                    >
                        {{ $t('buttons.create') }}
                    </a-button>
                </div>
            </form>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import categoriesApi from '@/api/categories';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    types: {
        type: Array,
        default: () => []
    },
    type: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['close', 'created']);

// Form data
const form = reactive({
    name: '',
    description: '',
    type: props.type,
    status: 'active'
});

// Form state
const isSubmitting = ref(false);
const errors = reactive({});
// Options
const typeOptions = ref(props.types);

const statusOptions = ref([
    { id: 'active', name: t('common.active') },
    { id: 'inactive', name: t('common.inactive') }
]);

// Form validation
const validateForm = () => {
    errors.value = {};

    if (!form.name.trim()) {
        errors.name = t('errors.required', { field: t('forms.category.name') });
        return false;
    }

    if (form.name.trim().length < 2) {
        errors.name = t('errors.min-length', { field: t('forms.category.name'), min: 2 });
        return false;
    }

    if (form.description && form.description.trim().length > 500) {
        errors.description = t('errors.max-length', { field: t('forms.category.description'), max: 500 });
        return false;
    }

    return true;
};

// Methods
const onSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await categoriesApi.admin.store({
            name: form.name.trim(),
            description: form.description.trim() || null,
            type: form.type || null,
            status: form.status
        });

        if (response.data.status == "SUCCESS") {
            emit('created', response.data.category);
            resetForm();
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } catch (error) {
        console.error('Error creating category:', error);
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors;
            Object.keys(serverErrors).forEach(key => {
                errors[key] = serverErrors[key][0];
            });
        } else {
            useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const onCancel = () => {
    if (!isSubmitting.value) {
        resetForm();
        emit('close');
    }
};

const resetForm = () => {
    form.name = '';
    form.description = '';
    form.type = '';
    form.status = 'active';
    Object.keys(errors).forEach(key => {
        errors[key] = '';
    });
};

// Watch for modal visibility to reset form
watch(() => props.visible, (newValue) => {
    if (newValue) {
        resetForm();
    }
});
</script>

<style scoped>
/* Add any custom styles here */
</style> 